import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { AccessService } from '@app-core/services/access/access.service';
import { DataService } from '@app-core/services/data/data.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { DRIVERS_PAGE_FILTER_OPTIONS, DRIVER_TABLE_PAGE_SIZE, ManageAction } from '../../common/driver-management.constants';
import { DriverManagementService } from '../../services/driver-management.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DriverDetails,
  DriverDetailsResponse,
  QueryParams,
  UnifiedDriverParams,
} from '@app-driver-management/common/driver-management.model';
import { Store } from '@ngrx/store';
import { State } from '@app-driver-management/reducers/driver-type.reducer';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { UpdateDriverType } from '@app-driver-management/actions/driver-type.action';
import { FormControl } from '@angular/forms';
import { DriverListComponent } from '@app-driver-management/components/driver-list/driver-list.component';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { asymmetricArrayDiff } from '@app-core/models/dirty-check';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { AsyncReportType } from '@app-core/constants/constants';
import { ConfirmDialogComponent } from '@app-shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AssignCoachComponent } from '@app-shared/components/assign-coach/assign-coach.component';
import { RequestDownloadReportBody } from '@app-core/models/core.model';
import { AssetConfigurationService } from '@app-asset-config/services/asset-configuration.service';

const tabNameIndexMapping = {
  drivers: 0,
  installers: 1,
};

interface DriverQrCodeDownload extends RequestDownloadReportBody {
  language?: string;
}
@Component({
  selector: 'app-driver-management',
  templateUrl: './driver-management.component.html',
  styleUrls: ['./driver-management.component.scss'],
})
export class DriverManagementComponent implements OnInit {
  @ViewChild(DriverListComponent) driverListComponent: DriverListComponent;
  @ViewChild(AssignCoachComponent) assignCoachComponent!: AssignCoachComponent;
  public loader: boolean = true;
  public currentMetricUnit: string;
  public csvLoader: boolean = false;
  public manageDriverAction = ManageAction;
  public driverList: DriverDetailsResponse;
  public fleetId: string;
  public currentDateFormat: string;
  public driverIdControl = new FormControl('');
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public driverFilterOptions = DRIVERS_PAGE_FILTER_OPTIONS;
  public driverTablePageSize: number = DRIVER_TABLE_PAGE_SIZE;
  public offset: number = 0;
  public totalDrivers: number;
  public currentTabIndex: number = 0;
  private tagList: Number[] = [];
  private oldDriverTags: Number[] = [];
  private tagSelected: boolean;
  public disableSubmitButton: boolean;
  public tagIds = new FormControl('');
  private searchValue: string;
  public exportLoader: boolean = false;
  private tagListChange$ = new Subject<Number[]>();
  public showContent: boolean = false;
  public selectedDriversCount: number;
  public selectedDriver: Array<[]>;
  public coachList: Array<[]>;
  public showLabel: boolean;
  public DriverIdsForAssignment: Array<[]>;
  public DriverTagsForAssignment: Array<[]>;
  public coachName: string;
  public currentLanguage: string;
  public selectedDriverIds: number[];
  private driverIdSource: string[];
  public isQRCodeEnabled: boolean;
  public qrCodeLoader: boolean = false;
  public qrCodeMessageKey: string;
  public qrCodeMessageLoader: boolean;
  constructor(
    public dataService: DataService,
    public translate: TranslateService,
    private driverManagementService: DriverManagementService,
    private gtmService: GoogleTagManagerService,
    private snackbarService: SnackBarService,
    private accessService: AccessService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<State>,
    private cdRef: ChangeDetectorRef,
    private asyncDownloadsService: AsyncDownloadsService,
    private dialog: MatDialog,
    public assetConfigService: AssetConfigurationService,
    private mixpanelService: MixPanelService
  ) {
    this.tagListChange$.pipe(debounceTime(300)).subscribe((tags) => {
      this.getUnifiedDriverList(true, '', tags);
    });
  }

  public ngOnInit() {
    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      this.fleetId = value;
      this.loader = true;
      this.getData(true);
      this.cdRef.detectChanges();
    });

    this.dataService._currentDateFormat.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentDateFormat = value;
      }
    });
    this.dataService._currentMetricUnit.subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
      }
    });

    this.driverIdControl.valueChanges.pipe(debounceTime(500), takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      this.onSearchingDriverId(value);
      this.searchValue = value;
    });
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((param: QueryParams) => {
      const currentTab = param.tab || 'drivers'; // Default to 'drivers' if undefined
      this.currentTabIndex = tabNameIndexMapping[currentTab] || 0;
    });
    this.dataService._currentLanguage.subscribe((value: string) => {
      this.currentLanguage = value;
    });
    this.getConfigurations();
  }

  public onPageTabChange(event: MatTabChangeEvent) {
    const [tabName = 'drivers'] = Object.entries(tabNameIndexMapping).find(([, index]) => index === event.index);
    this.router.navigate([], {
      queryParams: {
        tab: tabName,
      },
      queryParamsHandling: 'merge',
    });
  }
  // unified Driver list
  public getUnifiedDriverList(isRefresh?: boolean, search?: string, tagIds?: Number[]) {
    const params: UnifiedDriverParams = {
      limit: this.driverTablePageSize,
      offset: this.offset,
      ...(search && { search: search }),
      ...(tagIds !== undefined && { 'tagIds[]': Array.isArray(tagIds) ? tagIds : [tagIds] }),
    };
    this.loader = true;
    this.driverManagementService
      .getUnifiedDriverList(isRefresh, params)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: DriverDetailsResponse) => {
          this.totalDrivers = res.totalCount;
          this.driverList = res;
          this.cdRef.detectChanges();
        },
        () => {
          this.driverList = null;
        }
      );
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getData(isRefresh?: boolean) {
    this.getUnifiedDriverList(isRefresh, '', this.tagList);
  }

  private onSearchingDriverId(value?: string) {
    this.getUnifiedDriverList(true, value);
  }

  public doRefresh() {
    this.driverIdControl.setValue('', { emitEvent: false });
    this.getData(true);
    this.driverListComponent.clearSelection();
    this.showContent = false;
    this.cdRef.detectChanges();
  }

  public navigateToEditDriver(action: ManageAction, driver?: DriverDetails) {
    this.router.navigate(['./edit-driver'], {
      relativeTo: this.route,
      queryParams: {
        action,
        ...(driver && driver.driverId ? { driverId: driver.driverId } : {}),
      },
    });
    this.mixpanelService.track('Add Driver clicked in Driver Page', {
      Action: action,
    });
  }

  public exportUsers() {
    this.csvLoader = null;
    this.exportLoader = true;
    const { customerName = '' } = this.accessService.getLoginInfo();
    const params = {
      userType: 'DRIVER',
      customerName,
      dateFormat: this.currentDateFormat,
      reportType: 'FLEET_DRIVERS',
    };
    this.mixpanelService.track('Export clicked in Driver Page', {
      'User Type': 'DRIVER',
      'Customer Name': customerName,
      'Date Format': this.currentDateFormat,
    });
    this.gtmService.exportDriversCSV(this.fleetId);
    this.asyncDownloadsService
      .requestDownloadReport(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.csvLoader = true;
          this.exportLoader = false;
        },
        (err) => {
          this.csvLoader = false;
          this.exportLoader = false;
          if (err?.status === 409) {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  public downloadQRcode(driverIds?: string[]) {
    this.qrCodeLoader = null;
    this.exportLoader = true;
    const params: DriverQrCodeDownload = {
      reportType: 'FLEET_DRIVERS_QR_CODES',
      language: this.currentLanguage, // You can fetch this from a language service if needed
      reportFormat: 'PDF',
      ...(driverIds?.length ? { driverIds } : {}),
    };
    this.gtmService.exportQRcode(this.fleetId);
    this.asyncDownloadsService
      .requestDownloadReport(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.qrCodeLoader = true;
          this.exportLoader = false;
          this.driverListComponent.clearSelection();
        },
        (err) => {
          this.qrCodeLoader = false;
          this.exportLoader = false;
          if (err?.status === 409) {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  public navigateToDownloads() {
    if (this.qrCodeLoader) {
      this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.fleetDriverQRCode.key);
      this.mixpanelService.track('Export Options Clicked - reports page');
    } else {
      this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.drivers.key);
      this.mixpanelService.track('Export Options Clicked - reports page');
    }
  }

  public driverTypeChanged(event: MatButtonToggleChange) {
    const displayEventName =
      event.value === 'manualAddition' ? 'Manual Tab Clicked - Driver Page' : 'External Source Clicked - Driver Page';
    this.mixpanelService.track(displayEventName, {
      Type: event.value,
    });
    this.store.dispatch(new UpdateDriverType({ driverType: event.value }));
  }

  onPageChange(event: { limit: number; offset: number }) {
    this.offset = event.offset;
    this.driverTablePageSize = event.limit;
    this.onSearchingDriverId(this.searchValue);
  }

  public selectedTags(tags: Number[]): void {
    // Check if the new tags are different from the current tagList
    const areTagsSame = this.tagList.length === tags.length && this.tagList.every((tag, index) => tag === tags[index]);

    if (!areTagsSame) {
      this.tagList = tags;
      this.tagListChange$.next(this.tagList);
    }

    const eventTagsIds: Number[] = (this.oldDriverTags || []).map((t: any) => t.tagId);
    const diff = asymmetricArrayDiff<Number>(tags || [], eventTagsIds);
    this.tagSelected = Boolean(diff.length);
    this.disableSubmitButton = !this.tagSelected;
  }
  public updateShowButton(value: boolean) {
    this.showContent = value; // Update when child emits event
  }
  public updateSelectedDriversCount(event: { count; drivers }) {
    this.selectedDriversCount = event.count;
    this.selectedDriver = event.drivers;
    this.DriverIdsForAssignment = event.drivers.map((driver) => driver.driverId);
    this.DriverTagsForAssignment = event.drivers.map((driver) => driver.tagIds);
  }
  public DeleteMultipleDriver() {
    this.driverListComponent.DeleteSelectedDrivers();
  }
  public handleClearSelection() {
    this.driverIdControl.setValue('', { emitEvent: false });
    this.getData(true);
    this.driverListComponent.clearSelection();
  }
  public openBulkConfirmWindow(event: [boolean, string]) {
    const [isConfirmed, coachName] = event;

    this.coachName = coachName;
    if (isConfirmed) {
      this.BulkCoachAssignment();
    }
  }
  public BulkCoachAssignment() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      position: { top: '24px' },
      minHeight: '190px',
      width: '332px',
      maxWidth: 'none',
      panelClass: ['delete-modal'],
      data: {
        title: this.translate.instant('Set as default coach?'),
        content: this.translate.instant(`Would you like to assign ${this.coachName} as the default coach for these drivers?`),
        actions: [
          { actionButton: this.translate.instant('DECLINE'), dialogCloseValue: false },
          { actionButton: this.translate.instant('ACCEPT'), dialogCloseValue: true, primary: true },
        ],
      },
    });

    dialogRef.afterClosed().subscribe((confirmAssignment: boolean) => {
      if (confirmAssignment) {
        this.assignCoachComponent.BulkCoachAssignment();
      }
    });
  }
  onSelectedDriverIdsChanged(driverIds: number[]) {
    this.selectedDriverIds = driverIds;
  }

  private getConfigurations() {
    this.assetConfigService.isLoadingConfig.next(true);
    this.assetConfigService
      .getFleetConfiguration()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.assetConfigService.isLoadingConfig.next(false);
        })
      )
      .subscribe((res: any) => {
        const { configuration = {} } = res || {};
        const categories = ['light', 'medium', 'heavy'];
        this.driverIdSource = categories.map((category) => configuration?.[category]?.driverIdSource || null);
        this.isQRCodeEnabled = this.driverIdSource.includes('qrcode');
      });
  }

  get exportMessageKey(): string {
    return this.qrCodeLoader ? 'qrCodeDownloadSuccessMessage' : 'exportDownloadInfoMessage';
  }
  onQRDownloadSuccess(messageKey: string) {
    this.qrCodeMessageKey = messageKey; // Set message to be shown in alert
    this.qrCodeMessageLoader = true;

    // Auto-hide the alert after, say, 5 seconds
    setTimeout(() => {
      this.qrCodeLoader = false;
    }, 5000);
  }
}
